/**
 * LoanProviderPicker: shared dropdown for selecting which loan provider's
 * rate the app should treat as canonical.
 *
 * State lives at the top of the store (selectedLoanProvider), so the same
 * picker rendered on Loop Loan, Carry Retirement, Salary Plan, and the
 * Purchases page all see and mutate the same value. Switching on any one
 * page propagates everywhere.
 *
 * Propagation rules (per Paul's "all rates should change, besides any
 * already entered"):
 *   - The Active APR tile on the Purchases page (which has no manual
 *     override stored) re-reads from the new provider's rate.
 *   - Loop Loan's loanApr in 'live' mode auto-updates to the new
 *     provider's rate.
 *   - Loop Loan's LLTV synced display switches sources.
 *   - Stored values (carryInputs.loanApr, loanEvents[].aprSnapshot,
 *     loopLoanInputs.loanApr in 'manual' mode, manualBorrowAprOverride)
 *     are NOT touched. Manual edits survive provider switches.
 *
 * The right-side rate display shows the currently-active provider's rate
 * with its "as of" timestamp, so the user can see both options' numbers
 * without opening the dropdown.
 */

import { useEffect } from 'react';
import { useAppStore } from '../state/store.js';
import type { LoanProvider } from '../engine/types.js';
import { formatPct } from './Money.js';

interface LoanProviderPickerProps {
  /** Optional label override. Default: "Loan provider". */
  label?: string;
  /** Compact mode: smaller font, single-line layout. Used in dense rows. */
  compact?: boolean;
}

export function LoanProviderPicker({ label = 'Loan provider', compact = false }: LoanProviderPickerProps): JSX.Element {
  const selected = useAppStore(s => s.selectedLoanProvider);
  const setProvider = useAppStore(s => s.setLoanProvider);
  const aaveSnap = useAppStore(s => s.aaveSnapshot);
  const lednSnap = useAppStore(s => s.lednSnapshot);
  // Patch DM: aaveStatus + lednStatus subscriptions removed. They were
  // previously used as deps for the self-heal effect below, but that
  // created a race where the effect cancelled its own in-flight fetch
  // when it set status='loading' (see comment block on the effect).
  // The UI keys off `activeSnap` (snapshot presence), never off status,
  // so we don't need to re-render when status changes.

  const activeSnap = selected === 'aave' ? aaveSnap : lednSnap;
  const fontSize = compact ? 'var(--fs-micro)' : 'var(--fs-small)';

  // Patch CE / DM: self-heal. App.tsx fires a one-shot boot-fetch for
  // each provider on mount, but several edge cases leave a snapshot
  // null afterward and the boot-fetch never re-runs:
  //   - sign-in/out triggers resetToDefaults which sets *Snapshot back
  //     to null AND *Status back to 'idle'; applyPersistedState doesn't
  //     restore them (session-only).
  //   - the boot-fetch errored (Aave network failure) and never retried.
  //   - the user changed providers and the new one's snapshot was never
  //     fetched this session.
  // This effect kicks off a fetch whenever the active provider's snap
  // is null. Matches the lazy-import pattern in App.tsx so we don't
  // pull these providers into the initial bundle.
  //
  // *** DM fix: race that made this effect deterministically wedge after
  // every sign-in. ***
  // The previous version included aaveStatus + lednStatus in the deps
  // array AND guarded the fetch with `status !== 'loading'`. Trace:
  //   1. Effect runs (snap=null, status='idle'). Guard passes. Async
  //      IIFE schedules.
  //   2. `await import(...)` yields; resumes; sets status='loading'.
  //   3. Zustand commits the status change; React schedules a re-render.
  //   4. `await fetchProvider()` yields; React flushes the re-render.
  //   5. Re-render sees status in deps changed, so React calls cleanup
  //      (sets cancelled=true) and re-runs the effect. New run's guard
  //      fails (status IS 'loading' now) so it does nothing.
  //   6. Original fetch resumes, checks `if (cancelled) return` -> yes,
  //      bails. setSnapshot + setStatus('ok') never fire.
  //   7. Final state: snap=null, status='loading'. UI checks `activeSnap`
  //      which is still null, so it shows "Loading..." forever. Even for
  //      Ledn, whose underlying fetch is a hardcoded constant - the
  //      `await import` microtask gap is enough for the race to bite.
  //
  // Fix: don't put status in deps, and don't guard on status. The status
  // field is set by this effect itself; including it in deps lets the
  // effect cancel its own fetch. Removing both makes the effect re-run
  // only on the things it should react to (provider change, snapshot
  // appearing/disappearing). Status setter calls are kept so the store
  // remains consistent for any future consumer, but they no longer drive
  // this effect's lifecycle.
  //
  // Duplicate-fetch protection: the only way the effect re-runs with
  // snap still null is if `selected` toggles or the snap is externally
  // reset to null. Each re-run cancels the previous via the cancellation
  // flag, so at most one fetch ever produces side effects per provider.
  // A rapid Aave<->Ledn toggle could fire several requests but they
  // each cancel cleanly and Ledn is in-memory anyway; for Aave the
  // /api/aave-borrow proxy is rate-limited and serves from its own
  // cache, so a few extra requests are not a concern.
  useEffect(() => {
    if (selected === 'ledn' && lednSnap === null) {
      let cancelled = false;
      (async () => {
        try {
          const { fetchLednBorrow } = await import('../io/live/ledn.js');
          const { setLednStatus, setLednSnapshot } = useAppStore.getState();
          setLednStatus('loading');
          const snap = await fetchLednBorrow();
          if (cancelled) return;
          setLednSnapshot(snap);
          setLednStatus('ok');
        } catch (e) {
          if (cancelled) return;
          const { setLednStatus } = useAppStore.getState();
          const msg = e instanceof Error ? e.message : String(e);
          setLednStatus('error', msg);
        }
      })();
      return () => { cancelled = true; };
    }
    if (selected === 'aave' && aaveSnap === null) {
      let cancelled = false;
      (async () => {
        try {
          const { DefiLlamaProvider } = await import('../io/live/defillama.js');
          const provider = new DefiLlamaProvider();
          const { defillamaQuery, setAaveStatus, setAaveSnapshot } = useAppStore.getState();
          setAaveStatus('loading');
          const snap = await provider.fetchUsdcBorrowApr(defillamaQuery);
          if (cancelled) return;
          setAaveSnapshot(snap);
          setAaveStatus('ok');
        } catch (e) {
          if (cancelled) return;
          const { setAaveStatus } = useAppStore.getState();
          const msg = e instanceof Error ? e.message : String(e);
          setAaveStatus('error', msg);
        }
      })();
      return () => { cancelled = true; };
    }
    return undefined;
  }, [selected, aaveSnap, lednSnap]);

  return (
    <div
      className="row"
      style={{
        gap: 8,
        alignItems: 'baseline',
        flexWrap: 'wrap',
        fontSize,
      }}
    >
      <span className="faint">{label}:</span>
      <select
        value={selected}
        onChange={e => setProvider(e.target.value as LoanProvider)}
        style={{ fontSize, minWidth: 90 }}
      >
        <option value="aave">Aave V3</option>
        <option value="ledn">Ledn</option>
      </select>
      {activeSnap ? (
        <span className="mono faint" style={{ fontSize: 'var(--fs-micro)' }}>
          {/* Show the current rate inline so a quick glance reveals what
              the user has selected. The fetchedAt / asOf affordance is
              page-specific (Loop Loan has its full "Live data" panel
              that surfaces the timestamp).

              Trailing rate-character indicator: Aave is a variable rate
              (DefiLlama-sourced, fluctuates with utilization and Aave
              governance), Ledn is a fixed rate (published tier schedule,
              changes only when Ledn announces). The user wants this
              distinction visible at a glance because it materially changes
              how to think about loan-cost projections: Aave's 4% today
              could be 8% next quarter, but Ledn's 11.49% is a contract. */}
          {formatPct(activeSnap.borrowApr)}
          {selected === 'ledn' ? (
            <span style={{ marginLeft: 6 }}>
              ({'tierLabel' in activeSnap ? activeSnap.tierLabel : 'standard'})
            </span>
          ) : null}
          <span style={{ marginLeft: 6 }}>
            {'\u00B7'} {selected === 'aave' ? 'variable' : 'fixed'}
          </span>
        </span>
      ) : (
        <span className="mono faint" style={{ fontSize: 'var(--fs-micro)' }}>
          loading{'\u2026'}
        </span>
      )}
    </div>
  );
}
